import { useState, useRef, useEffect, useCallback } from "react";
import type { KeyboardEvent } from "react";
import {
  CATEGORY_LABELS,
  detectOpenTag,
  filterTags,
  type EmotionTag,
} from "../lib/emotionTags";

interface Props {
  value: string;
  onChange: (next: string) => void;
  /** When false, behaves as a plain textarea (no popover, no key handlers). */
  enabled: boolean;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
}

const MAX_SUGGESTIONS = 8;

/**
 * Textarea with intellisense for Fish Audio S2-Pro emotion tags.
 *
 * When the user types `[`, a filterable popover appears below the textarea
 * showing matching tags from the canonical vocabulary. Typing more chars
 * narrows the list. Enter/Tab inserts the highlighted tag as `[tag] ` and
 * dismisses; Esc dismisses without inserting.
 *
 * The popover is anchored below the textarea (not at the caret pixel) — much
 * simpler than a hidden mirror div for caret positioning, and works fine on
 * mobile.
 */
export function EmotionTagAutocomplete({
  value,
  onChange,
  enabled,
  placeholder,
  className,
  autoFocus,
}: Props) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [open, setOpen] = useState(false);
  const [partial, setPartial] = useState("");
  const [openPos, setOpenPos] = useState(0);
  const [highlight, setHighlight] = useState(0);
  const [suggestions, setSuggestions] = useState<EmotionTag[]>([]);

  // Recompute open-tag state from current value + caret on every change/click.
  const recompute = useCallback(() => {
    if (!enabled) {
      setOpen(false);
      return;
    }
    const ta = textareaRef.current;
    if (!ta) return;
    const caret = ta.selectionStart ?? 0;
    const detected = detectOpenTag(value, caret);
    if (!detected) {
      setOpen(false);
      return;
    }
    const matches = filterTags(detected.partial).slice(0, MAX_SUGGESTIONS);
    if (matches.length === 0) {
      setOpen(false);
      return;
    }
    setOpen(true);
    setPartial(detected.partial);
    setOpenPos(detected.openPos);
    setSuggestions(matches);
    setHighlight(0);
  }, [value, enabled]);

  useEffect(() => {
    recompute();
  }, [recompute]);

  // Closing the popover when the model is disabled mid-session
  useEffect(() => {
    if (!enabled) setOpen(false);
  }, [enabled]);

  const insertTag = useCallback(
    (tag: EmotionTag) => {
      const ta = textareaRef.current;
      if (!ta) return;
      const caret = ta.selectionStart ?? 0;
      const before = value.slice(0, openPos);
      const after = value.slice(caret);
      const inserted = `[${tag.tag}] `;
      const next = before + inserted + after;
      onChange(next);
      setOpen(false);
      // Move caret to right after the inserted "[tag] "
      const newCaret = openPos + inserted.length;
      requestAnimationFrame(() => {
        ta.focus();
        ta.setSelectionRange(newCaret, newCaret);
      });
    },
    [onChange, openPos, value],
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => (h + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (h - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      insertTag(suggestions[highlight]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onClick={recompute}
        onKeyUp={recompute}
        placeholder={placeholder}
        className={className}
        autoFocus={autoFocus}
      />

      {open && (
        <div
          className="absolute left-0 right-0 top-full mt-1 z-20 max-h-64 overflow-y-auto
                     rounded-lg border border-slate-700 bg-slate-900 shadow-xl"
          // Block focus loss from the textarea when interacting with the popover
          onMouseDown={(e) => e.preventDefault()}
        >
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-800">
            Emotion tag{partial && <> · filtering "{partial}"</>}
          </div>
          {suggestions.map((tag, i) => (
            <button
              key={tag.tag}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                insertTag(tag);
              }}
              onMouseEnter={() => setHighlight(i)}
              className={`w-full text-left px-3 py-1.5 flex items-center gap-2 text-sm
                         ${
                           i === highlight
                             ? "bg-indigo-500/20 text-slate-100"
                             : "text-slate-300"
                         }`}
            >
              <span className="font-mono text-amber-400">[{tag.tag}]</span>
              <span className="text-[10px] text-slate-500 ml-auto">
                {CATEGORY_LABELS[tag.category]}
              </span>
            </button>
          ))}
          <div className="px-3 py-1 text-[10px] text-slate-600 border-t border-slate-800">
            ↑↓ navigate · Enter/Tab insert · Esc dismiss · freeform tags also supported
          </div>
        </div>
      )}
    </div>
  );
}
